import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './TermsModal.css';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline?: () => void;
  mandatory?: boolean;
  isSubmitting?: boolean;
}

type TabType = 'terms' | 'privacy';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const TermsModal: React.FC<TermsModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  onDecline,
  mandatory = false,
  isSubmitting = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('terms');
  const agreeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen || !mandatory) return;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.classList.add('terms-gate-active');
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (event.key !== 'Tab') return;

      const modalRoot = document.getElementById('terms-modal-root');
      if (!modalRoot) return;

      const focusable = Array.from(
        modalRoot.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => element.offsetParent !== null || element === document.activeElement);

      if (focusable.length === 0) {
        // Nothing focusable inside the gate: keep focus pinned to the modal itself.
        event.preventDefault();
        return;
      }

      const firstFocusable = focusable[0];
      const lastFocusable = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      // If focus has escaped the dialog, pull it back in before cycling.
      if (!(activeElement instanceof Node) || !modalRoot.contains(activeElement)) {
        event.preventDefault();
        firstFocusable.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    agreeButtonRef.current?.focus();

    return () => {
      document.body.classList.remove('terms-gate-active');
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, mandatory]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      id="terms-modal-root"
      className={`terms-overlay ${mandatory ? 'terms-overlay--mandatory' : ''}`}
      role="presentation"
      onClick={mandatory ? undefined : onClose}
    >
      <div
        className={`terms-modal ${mandatory ? 'terms-modal--mandatory' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-title"
        aria-describedby={mandatory ? 'terms-mandatory-message' : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="terms-header">
          <div className="terms-header-copy">
            <h2 id="terms-title" className="terms-title">
              {mandatory ? 'Accept Terms to Continue' : 'Legal Information'}
            </h2>
            {mandatory && (
              <p id="terms-mandatory-message" className="terms-mandatory-message">
                Review and accept our Terms and Privacy Policy to use JigsawML. The app stays
                locked until you agree.
              </p>
            )}
          </div>
          {!mandatory && (
            <button type="button" className="terms-close-button" onClick={onClose} aria-label="Close Modal">✕</button>
          )}
        </div>

        <div className="terms-tabs">
          <button
            type="button"
            className={`terms-tab-button ${activeTab === 'terms' ? 'active' : ''}`}
            aria-pressed={activeTab === 'terms'}
            onClick={() => setActiveTab('terms')}
          >
            Terms and Conditions
          </button>
          <button
            type="button"
            className={`terms-tab-button ${activeTab === 'privacy' ? 'active' : ''}`}
            aria-pressed={activeTab === 'privacy'}
            onClick={() => setActiveTab('privacy')}
          >
            Privacy Policy
          </button>
        </div>

        <div className="terms-content">
          {activeTab === 'terms' && (
            <div className="terms-tab-content">
              {/* --- TERMS AND CONDITIONS --- */}
          <h3 className="terms-section-title">JigsawML — Terms and Conditions</h3>

          <p><strong>Last updated:</strong> July 8, 2026</p>

          <p>These Terms and Conditions govern your access to and use of the JigsawML website, applications, IDE extensions, APIs, and related services provided by JigsawML.</p>

          <p>By creating an account, clicking "I agree", connecting a repository, or otherwise accessing or using the Service, you agree to be bound by these Terms. <strong>If you do not agree, do not use the Service.</strong></p>

          <hr />

          <h3>1. The Service</h3>
          <p>JigsawML scans source code repositories that you provide or connect, and generates architecture maps, summaries, diagrams, explanations, and related prompts/context. To do this, the Service may download, copy, compress, store, parse, and analyze your code and process it using automated tools and third-party artificial intelligence providers.</p>

          <h3>2. Eligibility and Accounts</h3>
          <p>You must be at least 18 years old (or the age of majority in your jurisdiction) and able to form a binding contract. You are responsible for your account credentials and for all activity under your account. Notify us immediately of any unauthorized use.</p>

          <h3>3. Your Content and Ownership</h3>
          <p>"<strong>Your Content</strong>" means any source code, repositories, files, text, or other materials you upload, connect, or submit to the Service. <strong>As between you and JigsawML, you retain all ownership of Your Content.</strong> You grant JigsawML a worldwide, non-exclusive, royalty-free license to host, copy, compress, store, transmit, parse, analyze, and process Your Content, and to create derived outputs (such as maps and summaries), <strong>solely for the purpose of operating, providing, securing, and improving the Service for you.</strong> We do not claim ownership of Your Content.</p>

          <h3>4. Your Representations and Warranties</h3>
          <p>You represent and warrant that:<br />
          (a) you own Your Content or have all rights, licenses, and permissions necessary to upload it and to grant the license in Section 3;<br />
          (b) Your Content and your use of the Service do not infringe or violate any third party's intellectual property, privacy, contractual, or other rights, or any law;<br />
          (c) you have authority to bind any organization on whose behalf you use the Service; and<br />
          (d) <strong>you will not upload secrets, credentials, API keys, passwords, personal data, or other sensitive information that you do not intend to be processed as described in these Terms.</strong></p>

          <h3>5. Acceptable Use</h3>
          <p>You agree not to: (a) upload code or data you do not have the right to upload; (b) use the Service for any unlawful purpose; (c) attempt to gain unauthorized access to the Service or other users' data; (d) reverse engineer, resell, or misuse the Service except as permitted; (e) introduce malware or disrupt the Service; or (f) use the Service to violate any applicable law or third-party rights.</p>

          <h3>6. Third-Party Services and AI Sub-Processors</h3>
          <p>To provide the Service, we use third-party infrastructure and AI providers (which may include, for example, cloud hosting providers and AI/LLM providers such as OpenAI and/or Microsoft Azure OpenAI). <strong>You acknowledge and agree that portions of Your Content (such as code snippets) may be transmitted to and processed by these third-party providers</strong> to generate summaries, maps, and other outputs. Such providers process data under their own terms and security practices. A current list of sub-processors is available on request or in our Privacy Policy.</p>
          <p><strong>We do not use Your Content to train our AI or machine-learning models.</strong> Where we use third-party AI/LLM providers, we select providers that state that Your Content is not used to train their models.</p>

          <h3>7. Data Storage and Security</h3>
          <p>We store Your Content (which may include a compressed copy of your repository) on infrastructure we control, and we apply reasonable technical and organizational measures designed to protect it. <strong>However, no method of transmission or storage is completely secure, and we cannot and do not guarantee absolute security.</strong> You acknowledge that you provide Your Content at your own risk and that, to the maximum extent permitted by law, JigsawML is not liable for unauthorized access to, loss of, or disclosure of Your Content except to the extent directly caused by our gross negligence or willful misconduct. You are responsible for maintaining your own backups of Your Content.</p>

          <h3>8. AI-Generated Output</h3>
          <p>Outputs generated by the Service (maps, summaries, explanations, prompts, suggested changes) are produced by automated and AI systems and <strong>may be inaccurate, incomplete, or unsuitable for your purpose.</strong> You are solely responsible for reviewing and validating any output before relying on it, including before applying any suggested change to your codebase. The Service does not provide professional, legal, or security advice.</p>

          <h3>9. Fees and Payment</h3>
          <p>Certain features require payment. Fees, billing cycles, and plans are described at purchase. Unless stated otherwise in these Terms or required by law, fees are non-refundable. We may change pricing prospectively with notice. You are responsible for applicable taxes.</p>
          <p><strong>Right to terminate subscriptions; pro-rated refunds.</strong> Notwithstanding any subscription term (including any annual or other multi-month term), <strong>we may terminate or discontinue any subscription, plan, or the Service, in whole or in part, at any time and for any reason (including convenience) upon reasonable notice.</strong> If we terminate a paid subscription before the end of a period for which you have already paid, and the termination is not due to your breach of these Terms, <strong>we will refund the pro-rated portion of the fees corresponding to the unused remainder of that paid period.</strong> Except where required by law, such a pro-rated refund is your sole and exclusive remedy for our termination of a subscription, and we will have no further liability to you for the termination.</p>

          <h3>10. Intellectual Property in the Service</h3>
          <p>The Service, including its software, design, and content (excluding Your Content), is owned by JigsawML and its licensors and is protected by law. We grant you a limited, revocable, non-exclusive, non-transferable license to use the Service in accordance with these Terms. We may use anonymized and aggregated data that does not identify you or Your Content to operate and improve the Service.</p>

          <h3>11. Disclaimer of Warranties</h3>
          <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, OR UNINTERRUPTED OR SECURE OPERATION. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE OR SECURE.</p>

          <h3>12. Limitation of Liability</h3>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, JIGSAWML AND ITS OFFICERS, EMPLOYEES, AND SUPPLIERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS INTERRUPTION, ARISING OUT OF OR RELATING TO THE SERVICE OR THESE TERMS, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID US IN THE THREE (3) MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR (B) $50. SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS, SO SOME OF THE ABOVE MAY NOT APPLY TO YOU.</p>

          <h3>13. Indemnification</h3>
          <p>You agree to defend, indemnify, and hold harmless JigsawML from any claims, damages, liabilities, costs, and expenses (including reasonable legal fees) arising out of or related to: (a) Your Content; (b) your breach of these Terms or your representations in Section 4; (c) your violation of any law or third-party right; or (d) your misuse of the Service.</p>

          <h3>14. Confidentiality</h3>
          <p>Each party may receive confidential information of the other. Each party agrees to protect the other's confidential information with reasonable care and to use it only as needed to perform under these Terms. Your Content is treated as your confidential information.</p>

          <h3>15. Termination</h3>
          <p>You may stop using the Service at any time. We may suspend or terminate your access if you breach these Terms or to protect the Service or other users. <strong>We may also terminate or discontinue any subscription or the Service for convenience at any time, including in the middle of an annual or other multi-month subscription term, upon reasonable notice; in that case, any pro-rated refund of prepaid, unused fees will be handled as described in Section 9.</strong> Upon termination, your right to use the Service ends. We will delete or return Your Content within a reasonable period in accordance with our data-retention practices and applicable law, except where retention is legally required.</p>

          <h3>16. Changes to the Service and Terms</h3>
          <p>We may modify the Service or these Terms at any time. If we make material changes, we will provide reasonable notice (for example, by posting the updated Terms with a new "Last updated" date or by email). Your continued use after changes take effect constitutes acceptance.</p>

          <h3>17. Governing Law and Disputes</h3>
          <p>These Terms are governed by the laws of Delaware, United States, without regard to its conflict-of-laws rules. The courts located in Boston, Massachusetts and San Francisco, California will have exclusive jurisdiction, and you consent to that jurisdiction.</p>

          <h3>18. Miscellaneous</h3>
          <p>These Terms are the entire agreement between you and JigsawML regarding the Service and supersede prior agreements. If any provision is held unenforceable, the rest remains in effect. Our failure to enforce a provision is not a waiver. You may not assign these Terms without our consent; we may assign them to a successor. Neither party is liable for delays caused by events beyond its reasonable control (force majeure).</p>

          <h3>19. Contact</h3>
          <p>
            For questions about these Terms, reach out to us at:{' '}
            <a href="mailto:info@jigsawml.com" className="terms-link">info@jigsawml.com</a>
          </p>

            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="terms-tab-content">
              {/* --- PRIVACY POLICY --- */}
          <h3 className="terms-section-title">JigsawML — Privacy Policy</h3>

          <p><strong>Last updated:</strong> June 24, 2026<br />
          <strong>Effective date:</strong> June 24, 2026</p>

          <p>This Privacy Policy explains how JigsawML collects, uses, shares, and protects information when you use the JigsawML website, applications, IDE extensions, APIs, and services. It should be read together with our Terms and Conditions. By using the Service, you agree to this Policy.</p>

          <hr />

          <h3>1. Information We Collect</h3>
          <p><strong>a) Account information.</strong> When you sign up (including via GitHub or Google OAuth), we collect your name, email address, profile identifier, and authentication tokens.</p>

          <p><strong>b) Your Content (source code).</strong> When you connect or upload a repository, we receive, copy, <strong>compress, and store</strong> your source code and related files, and metadata such as file names, structure, and commit information, in order to scan it and generate architecture maps and summaries.</p>

          <p><strong>c) Usage and device data.</strong> We automatically collect logs, IP address, browser/device type, pages and features used, timestamps, and similar diagnostic and analytics data (including via tools such as application monitoring / RUM).</p>

          <p><strong>d) Payment information.</strong> If you purchase a paid plan, payment is processed by a third-party payment processor. We do not store your full card details; we receive limited billing metadata (e.g., plan, status, last four digits).</p>

          <p><strong>e) Communications.</strong> Information you provide when you contact support or respond to us.</p>

          <p><strong>f) Cookies and similar technologies.</strong> Used for authentication, preferences, and analytics (see Section 9).</p>

          <h3>2. How We Use Your Information</h3>
          <p>We use the information to:</p>
          <ul>
            <li>provide, operate, and maintain the Service (including scanning your code and generating maps, summaries, prompts, and other outputs);</li>
            <li>authenticate you and secure accounts;</li>
            <li>process payments and manage subscriptions;</li>
            <li>monitor, debug, secure, and improve the Service;</li>
            <li>communicate with you about the Service, updates, and support;</li>
            <li>comply with legal obligations and enforce our Terms.</li>
          </ul>
          <p>We use <strong>anonymized and aggregated data</strong> (which does not identify you or your code) to analyze and improve the Service.</p>
          <p><strong>We do not use Your Content or personal information to train AI or machine-learning models.</strong> Your code and data are used solely to provide the Service to you (for example, to build and maintain your architecture map and generate outputs).</p>

          <h3>3. AI Processing of Your Code — Important</h3>
          <p>To generate architecture maps and summaries, <strong>portions of Your Content (such as code snippets, file names, and structure) are transmitted to and processed by third-party AI/LLM providers</strong> (which may include OpenAI (including via Microsoft Azure OpenAI) and Anthropic). These providers process the data to return outputs to us and handle data under their own privacy and security terms and data-retention policies. We send only what is needed to provide the Service. You consent to this processing by using the Service. <strong>Please do not submit secrets, credentials, or sensitive personal data within your code.</strong></p>
          <p><strong>We do not use Your Content or personal information to train our AI or machine-learning models.</strong> Your Content may be shared with AI/LLM providers that state it is not used to train their models. For additional privacy control, you may choose to run JigsawML locally using our installer and your own LLM credentials.</p>

          <h3>4. How We Share Information</h3>
          <p>We do <strong>not sell</strong> your personal information or Your Content. We share information only with:</p>
          <ul>
            <li><strong>Sub-processors / service providers</strong> that help us run the Service, such as: cloud hosting and storage (e.g., AWS), AI/LLM providers (e.g., OpenAI), payment processors, analytics, and email providers. They may access data only to perform services for us and are bound by confidentiality and data-protection obligations.</li>
            <li><strong>Legal and safety</strong> reasons: to comply with law, legal process, or lawful requests, or to protect the rights, safety, and security of JigsawML, our users, or the public.</li>
            <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets, with notice to you.</li>
          </ul>

          <h3>5. Data Retention</h3>
          <p>We retain Your Content and personal data for as long as your account is active or as needed to provide the Service, and thereafter only as required to comply with legal obligations, resolve disputes, and enforce agreements. <strong>You may request deletion of Your Content and account at any time</strong> (see Section 7). On deletion, we remove or anonymize the data within a reasonable period, except where retention is legally required.</p>

          <h3>6. Data Security</h3>
          <p>We apply reasonable technical and organizational measures designed to protect your data, which may include encryption in transit, access controls, and isolation of stored repositories. <strong>However, no system is completely secure and we cannot guarantee absolute security.</strong> You use the Service and submit Your Content at your own risk, and you are responsible for keeping your own backups. If we become aware of a data breach affecting your personal data, we will notify you and the relevant authorities as required by applicable law.</p>

          <h3>7. Your Rights</h3>
          <p>Depending on your location, you may have the right to: access the personal data we hold about you; correct inaccurate data; delete your data; export/port your data; object to or restrict certain processing; and withdraw consent at any time. To exercise these rights, contact us at info@jigsawml.com. We will respond within the time required by applicable law. You may also have the right to lodge a complaint with your local data-protection authority.</p>

          <h3>8. Data Location and International Transfers</h3>
          <p>We store and process data on infrastructure located in the United States. If you access the Service from another country, your information may be transferred to and processed in countries that may have different data-protection laws. Where required, we use appropriate safeguards for such transfers.</p>

          <h3>9. Cookies and Tracking</h3>
          <p>We use cookies and similar technologies for authentication, remembering preferences, and analytics. You can control cookies through your browser settings; disabling some cookies may affect functionality.</p>

          <h3>10. Children's Privacy</h3>
          <p>The Service is not directed to children under 18 (or the applicable age of majority), and we do not knowingly collect their personal data. If you believe a child has provided us data, contact us and we will delete it.</p>

          <h3>11. Changes to This Policy</h3>
          <p>We may update this Policy from time to time. We will post the updated version with a new "Last updated" date and, for material changes, provide additional notice. Continued use after changes take effect constitutes acceptance.</p>

          <h3>12. Contact Us</h3>
          <p>
            For privacy questions, reach out to us at:
            <a href="mailto:info@jigsawml.com" className="terms-link">info@jigsawml.com</a>
          </p>

          {/* <h3>13. Data Protection Officer</h3>
          <p>Our Data Protection Officer is available at info@jigsawml.com. You may contact them with any concern about how we handle your personal data; we will address it within the timeframe required by applicable law.</p> */}

            </div>
          )}

          <div className="terms-ack-box">
            <p className="terms-ack-text"><em>By using JigsawML, you acknowledge that you have read, understood, and agree to these Terms and to our Privacy Policy.</em></p>
          </div>
        </div>

        <div className="terms-footer">
          {mandatory && onDecline && (
            <button
              type="button"
              className="terms-decline-button"
              onClick={onDecline}
              disabled={isSubmitting}
            >
              Decline & Sign Out
            </button>
          )}
          <button
            ref={agreeButtonRef}
            type="button"
            className="terms-agree-button"
            onClick={onAccept}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "I Understand & Agree"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TermsModal;
